// src/lightTheme.ts
//
// Roommatik UI v3 (warm neutrals + "Pattern A" shell) — 3c hybrid direction for
// internal engineering tools. Ported from the reference implementation in
// roommatik-analytics.ui (Insight) / roommatik.console.ui:
//   - warm cream canvas (#F7F5F1), white cards, solid desaturated teal rail (#256B7E)
//   - v3.12 card treatment: light sand border (#D6C6A8) + soft warm shadow
//   - one MuiButton system: teal-ink solid fill (#0B5062), white outlined
//   - rebalanced typography (bigger, lighter) + teal eyebrow (overline)
// Module augmentation (custom / blue / white / TypeBackground / variants) lives
// in src/types/theme.d.ts.
import { defaultTheme } from 'react-admin';
import { alpha, createTheme, Palette, Theme, PaletteOptions } from '@mui/material/styles';

// React-admin component overrides (keys unknown to MUI's Components map)
declare module '@mui/material/styles' {
  interface Components {
    RaList?: { styleOverrides?: Record<string, any> };
    RaListToolbar?: { styleOverrides?: Record<string, any> };
    RaFilterForm?: { styleOverrides?: Record<string, any> };
    RaTopToolbar?: { styleOverrides?: Record<string, any> };
    RaDatagrid?: { styleOverrides?: Record<string, any> };
  }
}

const radius = {
    small: '6px',    // Buttons, inputs, menu items
    medium: '7px',   // Compact toolbar controls
    large: '10px',   // Large containers
    card: '10px',    // Cards, tables, popovers — dedicated token
    chip: '9999px',  // Chips/badges render as fully-rounded pills
    full: '9999px',  // Circular elements
};

// v3 outline system. `borderStrong` drives the datagrid header line and neutral
// chips; `borderRow` is the soft in-table row separator.
const borderStrong = '#57534C';
const borderRow = '#D9D3C8';

// v3.12: card treatment = light SAND border + a soft warm shadow — card
// definition comes from the shadow, not a dark line.
const borderCard = '#D6C6A8';
const cardShadow = '0 6px 16px -12px rgba(63,49,25,0.35)';

// Solid-button fill (v3) — a dark teal-ink, distinct from Signal Teal
// (primary.main #0BA5B7) so filled CTAs don't clash with the teal chrome.
const CTA_FILL = '#0B5062';
const CTA_FILL_HOVER = '#0A4552';

// v2 teal-as-text (eyebrows, inline links) — darker than Signal Teal for AA.
const BRAND_TEXT_TEAL = '#147A87';

const custom = {
    radius,
    shadows: {
        none: '0 1px 2px 0 rgb(0 0 0 / 0.01)',
        subtle: '0 1px 2px rgba(20,40,60,0.04), 0 4px 16px rgba(20,40,60,0.05)',
        medium: '0 4px 12px rgba(20,40,60,0.10), 0 12px 32px rgba(20,40,60,0.08)',
        strong: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
        login: '0 25px 50px -12px rgb(0 0 0 / 0.15)',
        loginButton: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    },
    focusRing: '0 0 0 3px rgba(11, 165, 183, 0.25)',
    ctaFill: CTA_FILL,
    ctaFillHover: CTA_FILL_HOVER,
    text: {
        tertiary: '#746E63',
        label: '#7D8A90',
        idMono: '#A4ABB5',
        brand: BRAND_TEXT_TEAL,
        brandStrong: '#0C5A66',
    },
    table: {
        headerBg: '#FAF8F3',
        headerBorder: borderStrong,
        rowBorder: borderRow,
        // Single source of truth for table-header label typography (v3.4).
        headCell: {
            fontSize: '0.875rem',
            fontWeight: 600,
            textTransform: 'none' as const,
            letterSpacing: '0.01em',
            lineHeight: 1.3,
            color: '#282624',
        },
    },
    login: {
        containerGradient: (palette: Palette) => `linear-gradient(135deg, ${palette.grey[50]} 0%, ${palette.grey[200]} 100%)`,
        topBarGradient: (palette: Palette) => `linear-gradient(90deg, ${palette.primary.main} 0%, ${palette.primary.light} 100%)`,
        fieldBackground: (palette: Palette) => palette.grey[50],
        logoFilter: 'drop-shadow(0 4px 6px rgb(0 0 0 / 0.1))',
    },
    layout: {
        desktopContainerPadding: '8px 8px 8px 8px',
        gap: '0px',
        mobileContainerPadding: '4px',
        mobileTopMargin: '16px',
        card: {
            borderRadius: radius.card,
            boxShadow: cardShadow,
            padding: { xs: '16px', sm: '24px' },
        },
    },
    userMenu: {
        paper: {
            width: '280px',
            borderRadius: radius.large,
            boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
        },
    },
    // v3.8bis: teal-ink disc with white initials (as in Insight).
    userAvatar: {
        size: '40px',
        bg: CTA_FILL,
        color: '#FFFFFF',
        iconSize: '24px',
    },
    // Solid desaturated Deep-Teal rail: items read in white/translucent-white.
    // Rail background lives in palette.background.menu (single source of truth).
    menu: {
        openWidth: '230px',
        closedWidth: '64px',
        border: 'rgba(0,0,0,0.08)',
        groupLabelColor: 'rgba(255,255,255,0.55)',
        item: {
            borderRadius: radius.small,
            margin: '2px 0',
            padding: '7px 8px',
            gap: '12px',
            color: () => 'rgba(255,255,255,0.85)',
            iconColor: () => 'rgba(255,255,255,0.7)',
            hover: {
                color: () => '#FFFFFF',
                backgroundColor: () => 'rgba(255,255,255,0.08)',
            },
            // Translucent white pill + full-white text/icon + 2px white marker.
            active: {
                color: () => '#FFFFFF',
                backgroundColor: () => 'rgba(255,255,255,0.16)',
                fontWeight: 600,
                marker: () => '#FFFFFF',
                hover: {
                    backgroundColor: () => 'rgba(255,255,255,0.22)',
                },
            },
            closedActiveIcon: {
                backgroundColor: () => 'rgba(255,255,255,0.16)',
                color: () => '#FFFFFF',
                borderRadius: '6px',
            },
        },
    },
};

// v3 warm-neutral palette (post-cooldown values). Canvas is a warm cream so
// white cards/tables read by contrast; the rail is a dark, desaturated teal
// surface (#256B7E, rev. v3.1b), distinct from the brand accents.
const lightPalette: PaletteOptions = {
    mode: 'light',
    primary: {
        main: '#0BA5B7',
        light: '#55C3D2',
        dark: '#0E7C8A',
        contrastText: '#FFFFFF',
    },
    // Warm grey secondary (v3) — no longer a cool slate.
    secondary: {
        main: '#77726A',
        light: '#A19A8D',
        dark: '#57534C',
        contrastText: '#FFFFFF',
    },
    background: {
        default: '#F7F5F1',
        paper: '#FFFFFF',
        appbar: '#FBF9F5',
        menu: '#256B7E',
    },
    text: {
        primary: '#282624',
        secondary: '#3C3934',
    },
    error: {
        main: '#FB7185',
        light: '#FDA4AF',
        dark: '#9F1239',
        contrastText: '#FFFFFF',
    },
    warning: {
        main: '#FB923C',
        light: '#FDBA74',
        dark: '#9A3412',
        contrastText: '#334155',
    },
    info: {
        main: '#3B82F6',
        light: '#60A5FA',
        dark: '#1E40AF',
        contrastText: '#FFFFFF',
    },
    success: {
        main: '#16A34A',
        light: '#4ADE80',
        dark: '#15803D',
        contrastText: '#FFFFFF',
    },
    divider: '#D9D3C8',
    // Warm neutral scale (cream→brown) driving canvas, borders and micro-tones.
    grey: {
        50: '#F7F5F1',
        100: '#EEEAE3',
        200: '#DFDBD1',
        300: '#CDC7BB',
        400: '#A19A8D',
        500: '#77726A',
        600: '#57534C',
        700: '#3C3934',
        800: '#2C2926',
        900: '#282624',
    },
    blue: {
        50: '#EFF6FF',
        100: '#DBEAFE',
        200: '#BFDBFE',
        300: '#93C5FD',
        400: '#60A5FA',
        500: '#3B82F6',
        600: '#2563EB',
        700: '#1D4ED8',
        800: '#1E40AF',
        900: '#1E3A8A',
    },
    white: '#FFFFFF',
};

// Crear el tema base primero
const baseTheme = createTheme({
    ...defaultTheme,
    sidebar: {
        width: 230,      // matches custom.menu.openWidth
        closedWidth: 64,  // matches custom.menu.closedWidth
    },
    palette: lightPalette,
    custom: custom,
    // Global default radius for components that don't reference custom.radius.
    shape: { borderRadius: 7 },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        fontSize: 14,
        htmlFontSize: 16,
        // v3 typography — rebalanced: more size, less weight. Weight 600 lives
        // ONLY in h1–h3, the datagrid header and active menu items.
        h1: { fontSize: '1.625rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.02em' },
        h2: { fontSize: '1.375rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.015em' },
        h3: { fontSize: '1.1875rem', fontWeight: 600, lineHeight: 1.35, letterSpacing: '-0.01em' },
        h4: { fontSize: '1.0625rem', fontWeight: 500, lineHeight: 1.4 },
        h5: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.45 },
        h6: { fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.45, letterSpacing: '0.01em' },
        body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.6 },
        body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.55 },
        caption: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.5, color: '#746E63' },
        // Teal eyebrow: short uppercase guide-label above important blocks.
        overline: {
            fontSize: '0.6875rem',
            fontWeight: 800,
            letterSpacing: '0.08em',
            lineHeight: 1.5,
            textTransform: 'uppercase' as const,
            color: BRAND_TEXT_TEAL,
        },
        button: { fontWeight: 500, letterSpacing: '0.02em' },
    },
});

// Ahora crear el tema final con los componentes que usan el tema completo
export const lightTheme = createTheme({
    ...baseTheme,
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                '.RaList-main .RaList-actions': {
                    borderBottom: 'none !important',
                    alignItems: 'center !important',
                    paddingBottom: '4px',
                },
                // v3: only the table is a card. Neutralise react-admin's implicit
                // List Card (.RaList-content) so it doesn't draw a second frame
                // around the datagrid (the datagrid paints its own card).
                '.RaList-content': {
                    border: 'none !important',
                    backgroundColor: 'transparent !important',
                    boxShadow: 'none !important',
                    borderRadius: 0,
                },
                // Visible focus halo on keyboard navigation (AA).
                '*:focus-visible': {
                    outline: 'none',
                    boxShadow: custom.focusRing,
                    borderRadius: radius.small,
                },
                'button:focus-visible, a:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible': {
                    outline: 'none',
                    boxShadow: custom.focusRing,
                },
            },
        },

        MuiButton: {
            defaultProps: {
                size: 'small' as const,
                // Flat contained buttons (no elevation shadow).
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: 500,
                    // Slightly rounded (7px), not pill — flat, compact.
                    borderRadius: radius.medium,
                    textTransform: 'none',
                    minHeight: '38px',
                    padding: '8px 14px',
                    boxShadow: 'none',
                    transition: 'background-color 0.3s ease, color 0.3s ease',
                    '&:hover': { boxShadow: 'none' },
                },
                // v3 solid CTA — dark teal-ink fill, not Signal Teal.
                contained: {
                    backgroundColor: CTA_FILL,
                    color: '#FFFFFF',
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: CTA_FILL_HOVER,
                        boxShadow: 'none',
                    },
                },
                // v3 secondary — white fill + neutral border + dark text.
                outlined: {
                    backgroundColor: '#FFFFFF',
                    borderColor: baseTheme.palette.divider,
                    color: baseTheme.palette.text.primary,
                    '&:hover': {
                        backgroundColor: baseTheme.palette.action.hover,
                        borderColor: baseTheme.palette.divider,
                    },
                },
            },
            variants: [
                {
                    props: { variant: 'loginButton' },
                    style: {
                        borderRadius: radius.small,
                        padding: baseTheme.spacing(1.5),
                        color: baseTheme.palette.primary.contrastText,
                        fontSize: '1rem',
                        textTransform: 'none',
                        fontWeight: 600,
                        backgroundColor: baseTheme.palette.primary.main,
                        transition: 'all 0.2s ease-in-out',
                        boxShadow: 'none',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: custom.shadows.loginButton,
                            backgroundColor: baseTheme.palette.primary.light,
                        },
                        '&:active': {
                            transform: 'translateY(0)',
                        },
                    }
                },
                {
                    // Create CTA — same v3 teal-ink fill as `contained`, kept as a
                    // named variant so screens can express "create" intent.
                    props: { variant: 'createButton' },
                    style: {
                        backgroundColor: CTA_FILL,
                        color: '#FFFFFF',
                        boxShadow: 'none',
                        padding: '8px 14px',
                        fontWeight: 500,
                        fontSize: '1rem',
                        minWidth: 'auto',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        '&:hover': {
                            backgroundColor: CTA_FILL_HOVER,
                        },
                        '& .MuiSvgIcon-root': {
                            fontSize: '1.25rem',
                            color: '#FFFFFF',
                        },
                        [baseTheme.breakpoints.down('sm')]: {
                            position: 'fixed',
                            right: '16px',
                            bottom: '16px',
                            width: '56px',
                            height: '56px',
                            borderRadius: radius.full,
                            padding: 0,
                            minWidth: '56px',
                            zIndex: 1300,
                            backgroundColor: CTA_FILL,
                            boxShadow: '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)',
                            justifyContent: 'center',
                            '& .MuiButton-startIcon': {
                                margin: 0,
                            }
                        }
                    }
                },
                {
                    props: { variant: 'actionButton' },
                    style: {
                        minWidth: 'auto',
                        width: '32px',
                        height: '32px',
                        // Neutral: white surface + thin divider border, muted icon.
                        color: baseTheme.palette.grey[500],
                        border: `1px solid ${baseTheme.palette.divider}`,
                        backgroundColor: '#FFFFFF',
                        padding: baseTheme.spacing(0.5, 1),
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.3s ease, color 0.3s ease',
                        '&:hover': {
                            backgroundColor: baseTheme.palette.grey[100],
                        },
                        '& .MuiSvgIcon-root': {
                            fontSize: '1.1rem',
                        },
                        [baseTheme.breakpoints.down('sm')]: {
                            width: '40px',
                            height: '40px',
                            padding: baseTheme.spacing(1),
                        },
                    },
                },
            ]
        },

        // v2 inline links/actions: teal-as-text, weight 700.
        MuiLink: {
            styleOverrides: {
                root: {
                    color: BRAND_TEXT_TEAL,
                    fontWeight: 700,
                    textDecorationColor: alpha(BRAND_TEXT_TEAL, 0.4),
                    '&:hover': {
                        textDecorationColor: BRAND_TEXT_TEAL,
                    },
                },
            },
        },

        MuiIconButton: {
            styleOverrides: {
                root: {
                    border: `1px solid ${baseTheme.palette.primary.dark}20`,
                    borderRadius: radius.small,
                    color: baseTheme.palette.primary.main,
                    width: 36,
                    height: 32,
                    '&:hover': {
                        backgroundColor: baseTheme.palette.primary.dark,
                        color: baseTheme.palette.common.white,
                    },
                },
                colorInherit: {
                    color: baseTheme.palette.text.secondary,
                    border: `1px solid ${baseTheme.palette.text.secondary}20`,
                    '&:hover': {
                        color: baseTheme.palette.primary.main,
                        backgroundColor: baseTheme.palette.action.hover,
                        border: `1px solid ${baseTheme.palette.primary.main}30`,
                    }
                },
                colorPrimary: {
                    color: baseTheme.palette.primary.main,
                    border: `1px solid ${baseTheme.palette.primary.main}30`,
                    '&:hover': {
                        backgroundColor: `${baseTheme.palette.primary.main}14`,
                    }
                },
                colorSecondary: {
                    color: baseTheme.palette.secondary.main,
                    border: `1px solid ${baseTheme.palette.secondary.main}30`,
                    '&:hover': {
                        backgroundColor: `${baseTheme.palette.secondary.main}14`,
                    }
                },
                colorError: {
                    color: baseTheme.palette.error.main,
                    border: `1px solid ${baseTheme.palette.error.main}30`,
                    '&:hover': {
                        backgroundColor: `${baseTheme.palette.error.main}14`,
                    }
                },
                colorWarning: {
                    color: baseTheme.palette.warning.main,
                    border: `1px solid ${baseTheme.palette.warning.main}30`,
                    '&:hover': {
                        backgroundColor: `${baseTheme.palette.warning.main}14`,
                    }
                },
                colorInfo: {
                    color: baseTheme.palette.info.main,
                    border: `1px solid ${baseTheme.palette.info.main}30`,
                    '&:hover': {
                        backgroundColor: `${baseTheme.palette.info.main}14`,
                    }
                },
                colorSuccess: {
                    color: baseTheme.palette.success.main,
                    border: `1px solid ${baseTheme.palette.success.main}30`,
                    '&:hover': {
                        backgroundColor: `${baseTheme.palette.success.main}14`,
                    }
                },
                sizeLarge: {
                    width: 48,
                    height: 48,
                },
                sizeSmall: {
                    width: 32,
                    height: 32,
                },
            },
        },

        MuiBadge: {
            defaultProps: {
                color: 'primary',
                max: 99,
                anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
            },
            styleOverrides: {
                badge: ({ theme }) => ({
                    borderRadius: theme.custom?.radius?.chip ?? '4px',
                    minWidth: 18,
                    height: 18,
                    padding: '0 4px',
                    border: `2px solid ${theme.palette.background.paper}`,
                    backgroundColor: theme.palette.primary.dark,
                    color: theme.palette.common.white,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    lineHeight: 1,
                }),
            },
        },

        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: '16px',
                    fontWeight: 500,
                    minWidth: 'auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    '& .MuiButton-text': {
                        backgroundColor: baseTheme.palette.action.hover,
                        color: baseTheme.palette.text.secondary,
                        border: `1px solid ${baseTheme.palette.divider}`,
                        '&:hover': {
                            backgroundColor: baseTheme.palette.action.selected,
                        }
                    },
                    '& .MuiButton-contained': {
                        backgroundColor: CTA_FILL,
                        color: '#FFFFFF',
                        border: `1px solid ${CTA_FILL}`,
                        '&:hover': {
                            backgroundColor: CTA_FILL_HOVER,
                            borderColor: CTA_FILL_HOVER,
                        }
                    },
                    '& .MuiSvgIcon-root': {
                        fontSize: '1.25rem',
                    },
                }
            }
        },

        RaList: {
            styleOverrides: {
                root: {
                    '& .RaList-main': {
                        paddingTop: 12,
                        paddingLeft: 16,
                        paddingRight: 16,
                    }
                }
            }
        },

        RaListToolbar: {
            styleOverrides: {
                root: {
                    alignItems: 'center',
                    gap: '8px',
                },
            },
        },

        RaFilterForm: {
            styleOverrides: {
                root: {
                    minHeight: 'auto !important',
                    alignItems: 'center',
                    padding: 0,
                    gap: baseTheme.spacing(1),
                    '& .RaFilterForm-filterFormInput .MuiFormControl-root': {
                        marginTop: 0,
                    },
                },
            },
        },

        RaTopToolbar: {
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent',
                    marginBottom: 0,
                },
            },
        },

        RaDatagrid: {
            styleOverrides: {
                root: {
                    backgroundColor: baseTheme.palette.background.paper,
                    border: `1px solid ${baseTheme.palette.grey[200]}`,
                    borderRadius: radius.card,
                    overflow: 'hidden',

                    // Header label typography comes from the single source of
                    // truth (custom.table.headCell); only structural styling here.
                    '& .RaDatagrid-headerCell': {
                        backgroundColor: custom.table.headerBg,
                        borderBottom: `1px solid ${custom.table.headerBorder}`,
                        padding: '10px 14px',
                        ...custom.table.headCell,
                    },

                    '& .RaDatagrid-row': {
                        backgroundColor: baseTheme.palette.background.paper,
                        transition: 'background-color 0.12s ease',
                        '&:hover': {
                            backgroundColor: baseTheme.palette.grey[100],
                        },
                    },

                    '& .RaDatagrid-rowCell': {
                        fontSize: '0.8125rem',
                        padding: '8px 14px',
                        borderBottom: `1px solid ${custom.table.rowBorder}`,
                        color: baseTheme.palette.text.primary,
                        '&:last-of-type': {
                            borderRight: 'none',
                        },
                    },

                    'tbody tr:last-child .RaDatagrid-rowCell': {
                        borderBottom: 'none',
                    },

                    '& .MuiTablePagination-root': {
                        borderTop: `1px solid ${baseTheme.palette.divider}`,
                        backgroundColor: baseTheme.palette.background.default,
                        padding: baseTheme.spacing(1),
                    },

                    '& .RaDatagrid-rowCell:has(button)': {
                        paddingTop: '6px',
                        paddingBottom: '6px',
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                    },

                    '& button.RaButton-link': {
                        fontSize: '0.8rem',
                        color: baseTheme.palette.text.secondary,
                        padding: '4px 8px',
                        borderRadius: radius.small,
                        transition: 'color 0.2s ease, background-color 0.2s ease',
                        '&:hover': {
                            color: baseTheme.palette.primary.main,
                            backgroundColor: `${baseTheme.palette.primary.light}10`,
                        },
                    },

                    '& button[aria-label="Delete"]': {
                        color: baseTheme.palette.primary.main,
                        width: '36',
                        '&:hover': {
                            color: baseTheme.palette.common.white,
                            backgroundColor: baseTheme.palette.error.main,
                        },
                    },
                },
            },
        },

        // v3.12: light sand 1px border + soft warm shadow — cards lift gently
        // off the warm canvas.
        MuiCard: {
            defaultProps: {
                elevation: 0,
            },
            styleOverrides: {
                root: {
                    backgroundColor: baseTheme.palette.background.paper,
                    borderRadius: radius.card,
                    boxShadow: cardShadow,
                    border: `1px solid ${borderCard}`,
                    marginBottom: baseTheme.spacing(1),
                },
            },
        },

        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: '8px 14px',
                }
            }
        },

        // v3 warm-outline chip system: neutral chip = tinted pill with a visible
        // dark warm border; coloured states = light wash + border and dark text
        // of the same hue. The `solid` variant is the one-per-view accent.
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: radius.chip,
                    fontWeight: 500,
                    backgroundColor: '#F1EDE6',
                    color: '#2C2926',
                    border: `1px solid ${borderStrong}`,
                },
                colorSecondary: {
                    backgroundColor: '#F1EDE6',
                    color: '#2C2926',
                    border: `1px solid ${borderStrong}`,
                },
                colorPrimary: {
                    backgroundColor: `${baseTheme.palette.primary.main}14`,
                    border: `1px solid ${baseTheme.palette.primary.dark}`,
                    color: baseTheme.palette.primary.dark,
                },
                colorSuccess: {
                    backgroundColor: `${baseTheme.palette.success.main}14`,
                    border: `1px solid ${baseTheme.palette.success.dark}`,
                    color: baseTheme.palette.success.dark,
                },
                colorWarning: {
                    backgroundColor: `${baseTheme.palette.warning.main}1F`,
                    border: `1px solid ${baseTheme.palette.warning.dark}`,
                    color: baseTheme.palette.warning.dark,
                },
                colorError: {
                    backgroundColor: `${baseTheme.palette.error.main}14`,
                    border: `1px solid ${baseTheme.palette.error.dark}`,
                    color: baseTheme.palette.error.dark,
                },
                colorInfo: {
                    backgroundColor: `${baseTheme.palette.info.main}14`,
                    border: `1px solid ${baseTheme.palette.info.dark}`,
                    color: baseTheme.palette.info.dark,
                },
            },
            variants: [
                // Intense solid accent — one standout pill per view, never for
                // repeated table status tints.
                {
                    props: { variant: 'solid' },
                    style: {
                        border: 'none',
                        backgroundColor: baseTheme.palette.primary.main,
                        color: baseTheme.palette.primary.contrastText,
                        fontWeight: 500,
                    },
                },
            ],
        },

        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
                size: 'small',
            },
            styleOverrides: {
                root: {
                    '& .MuiInputBase-root': {
                        minHeight: '48px',
                    },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: '22px',
                    paddingTop: '8.5px',
                    paddingBottom: '8.5px',
                },
                icon: {
                    top: 'calc(50% - 12px)',
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    '&.MuiInputBase-sizeSmall': {
                        minHeight: '48px',
                    },
                    '&:not(.MuiInputBase-sizeSmall)': {
                        minHeight: '56px',
                    },
                },
                input: {
                    '&.MuiInputBase-inputSizeSmall': {
                        paddingTop: '8.5px',
                        paddingBottom: '8.5px',
                    },
                },
            },
        },

        MuiInputLabel: {
            styleOverrides: {
                root: {
                    '&.MuiInputLabel-sizeSmall': {
                        transform: 'translate(14px, 9px) scale(1)',
                    },
                    '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                    },
                },
            },
        },

        MuiFormControl: {
            styleOverrides: {
                root: {
                    marginTop: 0,
                    marginBottom: 0,
                },
            },
        },

        MuiAutocomplete: {
            styleOverrides: {
                inputRoot: {
                    paddingTop: '4px',
                    paddingBottom: '4px',
                    '&.MuiInputBase-sizeSmall': {
                        paddingTop: '2px',
                        paddingBottom: '2px',
                        '& .MuiAutocomplete-input': {
                            paddingTop: '6.5px',
                            paddingBottom: '6.5px',
                        },
                    },
                },
            },
        },

        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    marginTop: '3px',
                    marginLeft: '14px',
                    marginRight: '14px',
                },
            },
        },
    },
});

export default lightTheme;
